import {
  statusAtExecutedRange,
  isEditPanelAllowedAtStatus,
  shouldShowPercentQuoted,
  tradeHasIceberg,
} from "@/components/widgets/tradeActionSummary/helper/colDefs/colDefsShared";
import { onNodeCollapseChangeAllSettingsToFalse } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import { getBlocksData } from "@/store/tradeBlockDataStoreACTIONS";
import tradeDataStore from "@/store/tradeDataStore";
import { AgGridReact } from "ag-grid-react";
import { Dispatch, SetStateAction } from "react";
import { handleTradeNotification } from "@/lib/notificationHelper";
import {
  closeContextMenuForERs,
  closeContextMenuForTrade,
  findERsWithStatusChange,
} from "./helper/contextMenuTracker";
import {
  IFindChangedDataOutput,
  IdefaultExpandedRowContext,
  ensureTradeFillsInitialized,
  flashCellInLegLevel,
  flashCellsInERLevel,
  handleCoverColumnVisibility,
  handleOPOrderFillsArriving,
  handleRPOrderFillsUpdated,
  handleShowOrHideCols,
  hideOnER,
  hideOnLeg,
  redrawRowsViaESID,
  refreshCell,
  refreshCellInLegLevel,
  refreshColInERLevel,
  showOnER,
  showOnLeg,
  syncERDetailRowData,
} from "./ActionSummaryTableHelperFunctions";

// Redrawing row Helpers
function handleRedrawingRowsWhenNoStatusChanges({
  trade,
  gridRef,
  expandedRowsContext,
}: {
  trade: IFindChangedDataOutput;
  gridRef: React.RefObject<AgGridReact<any>>;
  expandedRowsContext: IdefaultExpandedRowContext[];
}) {
  const esid = trade.id;
  if (
    trade.currentStatus != ExecutionStrategyStatus.New &&
    trade.changes.status == undefined
  ) {
    // ONLY redraw when the unwind value in the legs is different.
    if (trade.role == "RP") {
      if (
        trade.changes.legs &&
        trade.changes.legs.newValue &&
        trade.changes.legs.newValue.length > 0 &&
        trade.changes.legs.oldValue &&
        trade.changes.legs.oldValue.length > 0
      ) {
        if (
          trade.changes.legs.newValue.length ==
          trade.changes.legs.oldValue.length
        ) {
          for (let i = 0; i < trade.changes.legs.oldValue.length; i++) {
            const oldValue = trade.changes.legs.oldValue[i].unwind;
            const newValue = trade.changes.legs.newValue[i].unwind;

            if (
              (oldValue == "Recoupon" && newValue != "Recoupon") ||
              (newValue == "Recoupon" && oldValue != "Recoupon")
            ) {
              redrawRowsViaESID(trade.id, gridRef);
            }
          }
        }
      }
    } else if (trade.role == "OP") {
      // Don't redraw while the legs table or the Edit Panel is open.
      let redrawOP = true;
      if (expandedRowsContext) {
        const thisContext = expandedRowsContext.find((row) => row.esid == esid);
        if (thisContext) {
          if (thisContext.legsShowing || thisContext.editPanelShowing) {
            redrawOP = false;
          }
        }
      }
      // Both views host the recoupon checkboxes, and a redraw rebuilds the detail
      // row underneath the user - annoying mid-edit if they are ticking several
      // cells. The deferred redraw fires on collapse via expandedRowsContext.
      if (redrawOP) {
        if (
          trade.changes.executionRequestCounterParties &&
          trade.changes.executionRequestCounterParties.newValue &&
          trade.changes.executionRequestCounterParties.newValue.length > 0 &&
          trade.changes.executionRequestCounterParties.oldValue &&
          trade.changes.executionRequestCounterParties.oldValue.length > 0
        ) {
          if (
            trade.changes.executionRequestCounterParties.newValue.length ==
            trade.changes.executionRequestCounterParties.oldValue.length
          ) {
            if (
              trade.changes.executionRequestCounterParties.newValue[0].legs &&
              trade.changes.executionRequestCounterParties.oldValue[0].legs &&
              trade.changes.executionRequestCounterParties.newValue[0].legs
                .length > 0 &&
              trade.changes.executionRequestCounterParties.oldValue[0].legs
                .length > 0
            ) {
              for (
                let i = 0;
                i <
                trade.changes.executionRequestCounterParties.oldValue[0].legs
                  .length;
                i++
              ) {
                const oldValue =
                  trade.changes.executionRequestCounterParties.oldValue[0].legs[
                    i
                  ].unwind;
                const newValue =
                  trade.changes.executionRequestCounterParties.newValue[0].legs[
                    i
                  ].unwind;

                if (
                  (oldValue == "Recoupon" && newValue != "Recoupon") ||
                  (newValue == "Recoupon" && oldValue != "Recoupon")
                ) {
                  redrawRowsViaESID(trade.id, gridRef);
                }
              }
            }
          }
        }
      }
    }
  }
}

// This is the brain of the logic that will determine when to redraw detail rows.
// Redrawing rows will cause the detail rendering to occur - needs to be used every time it's expected that the structure of a table is required. Aim is to not over do this as it may cause flickering with the tables and will break the API link on the tables.
export function handleRedrawingRowsForActionSummaryTable({
  gridRef,
  trade,
  expandedRowsContext,
  setExpandedRowsContext,
  setExpandedRowsContextMostRecentlyUpdated,
  getAccessTokenSilently,
}: {
  gridRef: React.RefObject<AgGridReact<any>>;
  trade: IFindChangedDataOutput;
  expandedRowsContext: IdefaultExpandedRowContext[];
  setExpandedRowsContext: Dispatch<
    SetStateAction<IdefaultExpandedRowContext[]>
  >;
  setExpandedRowsContextMostRecentlyUpdated: React.Dispatch<
    React.SetStateAction<string>
  >;
  getAccessTokenSilently: Function;
}) {
  if (trade.changes) {
    const esid = trade.id;
    refreshCell(gridRef, esid, "ActionSummaryStatusCell"); // Forces the status to update on each data change.
    refreshCell(gridRef, esid, "ActionSummaryIndicativeMid");

    // ER status changes can arrive without a trade status change.
    closeContextMenuForERs(
      esid,
      findERsWithStatusChange(
        trade.changes.executionRequestCounterParties?.oldValue,
        trade.changes.executionRequestCounterParties?.newValue,
      ),
    );

    if (trade.changes.status) {
      // Before any redraw below destroys the detail grids the menu is on.
      closeContextMenuForTrade(esid);

      // Play notification sound and log message for status changes
      handleTradeNotification({
        newStatus: trade.changes.status.newValue,
        oldStatus: trade.changes.status.oldValue,
        tradeId: trade.id,
        role: trade.role,
        owner: trade.owner,
        latestActivity: trade.latestActivity,
        showConsoleLog: true,
      });

      // LQC-1653 / LQC-2860: close an open Edit Panel once the new status no
      // longer offers it (Awarded, Working, Expired, Not Dealt ...). The context
      // change redraws the detail as the default view for that status.
      const editPanelOpenForTrade = expandedRowsContext.some(
        (ctx) => ctx.esid === esid && ctx.editPanelShowing,
      );
      if (
        editPanelOpenForTrade &&
        !isEditPanelAllowedAtStatus(
          trade.changes.status.newValue,
          trade.tradeType,
        )
      ) {
        onNodeCollapseChangeAllSettingsToFalse(
          expandedRowsContext,
          setExpandedRowsContext,
          esid,
          setExpandedRowsContextMostRecentlyUpdated,
        );
      }

      if (
        trade.changes.status.oldValue == ExecutionStrategyStatus.New || // old status of new means it has changed to not new.
        trade.changes.status.newValue == ExecutionStrategyStatus.New
      ) {
        onNodeCollapseChangeAllSettingsToFalse(
          // Set the trade settings to no longer be showing the CP list.
          expandedRowsContext,
          setExpandedRowsContext,
          trade.id,
          setExpandedRowsContextMostRecentlyUpdated,
        );
        // redrawRowsViaESID(trade.id, gridRef); // id is an esid
        refreshCell(gridRef, esid, "ActionSummaryBid");
        refreshCell(gridRef, esid, "ActionSummaryAsk");
        refreshCell(gridRef, esid, "ActionSummaryMid");
      } else if (
        trade.changes.status.newValue == ExecutionStrategyStatus.ReCreated
      ) {
        onNodeCollapseChangeAllSettingsToFalse(
          // Set the trade settings to no longer be showing the CP list.
          expandedRowsContext,
          setExpandedRowsContext,
          trade.id,
          setExpandedRowsContextMostRecentlyUpdated,
        );
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.changes.status.oldValue == ExecutionStrategyStatus.NotDealt || // old status of new means it has changed to not new.
        trade.changes.status.newValue == ExecutionStrategyStatus.Cancelled
      ) {
        getBlocksData({
          esid: esid,
          getAccessTokenSilently: getAccessTokenSilently,
        });
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.changes.status.newValue == ExecutionStrategyStatus.ReSent &&
        trade.changes.status.oldValue == ExecutionStrategyStatus.Recalled
      ) {
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        (trade.changes.status.newValue == ExecutionStrategyStatus.Working &&
          trade.changes.status.oldValue == ExecutionStrategyStatus.Received) ||
        trade.changes.status.oldValue == ExecutionStrategyStatus.ReSent ||
        trade.changes.status.oldValue == ExecutionStrategyStatus.Acknowledged
      ) {
        // Orders RP Specific
        // When status changes to "Working", initialize fills and redraw
        // so AG Grid re-evaluates masterDetail for the Order fills table
        ensureTradeFillsInitialized({ esid: trade.id });
        redrawRowsViaESID(trade.id, gridRef);
        // FOR the OP - Is this needed?
        // } else if (
        //   trade.changes.status.newValue == ExecutionStrategyStatus.Working &&
        //   trade.changes.status.oldValue == ExecutionStrategyStatus.Sent
        // ) {
        //   // Orders OP Specific - Should refresh and
        //   redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.tradeType === TradeType.Order &&
        (trade.changes.status.newValue === ExecutionStrategyStatus.Expired ||
          trade.changes.status.oldValue === ExecutionStrategyStatus.Expired)
      ) {
        // Rebuild leg rows from the store so unsent fill rows from Working are dropped
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.tradeType === TradeType.Order &&
        (trade.changes.status.newValue === ExecutionStrategyStatus.Filled ||
          trade.changes.status.newValue === ExecutionStrategyStatus.Affirmed ||
          trade.changes.status.newValue ===
            ExecutionStrategyStatus.NoMoreFills ||
          trade.changes.status.newValue ===
            ExecutionStrategyStatus.ChangePending)
      ) {
        // Orders: When status changes to Filled, Affirmed, NoMoreFill, or ChangePending
        // Redraw to update fills table column visibility (dealt price) and button visibility
        // AG Grid caches masterDetail evaluation, so we need to redraw for it to re-evaluate
        redrawRowsViaESID(trade.id, gridRef);

        // For OP, expand blocks that have fills to show the nested fills table
        if (trade.role === "OP") {
          handleOPOrderFillsArriving(trade, gridRef);
        }
      } else if (
        trade.tradeType === TradeType.Order &&
        trade.changes.status.oldValue === ExecutionStrategyStatus.ChangePending
      ) {
        // Orders: When status changes FROM ChangePending (back to Filled/NoMoreFill or to Working)
        // Redraw to update fills table column visibility (dealt price)
        redrawRowsViaESID(trade.id, gridRef);
      } else if (trade.changes.unwind) {
        // In this situation - A trade is either getting SET / REMOVED NPV - Status NOT DEALT => ReSent.
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.unwind &&
        trade.role === "RP" &&
        statusAtExecutedRange.includes(trade.changes.status.newValue)
      ) {
        // NPV unwind pack RP moving to Executed — regenerate column defs so quotedSize is re-evaluated.
        redrawRowsViaESID(trade.id, gridRef);
      }
    } else {
      // A CHANGE IS NOTICED BUT NO STATUS CHANGE.

      // Re-quote detection: status is Quoted and latestActivity is Quoted but no status change —
      // the RP updated their quote while the trade was already at Quoted.
      // Only fire if actionedAt is within the last 5 seconds to avoid stale re-diffs.
      if (
        trade.currentStatus === ExecutionStrategyStatus.Quoted &&
        trade.latestActivity === ExecutionStrategyStatus.Quoted &&
        trade.changes.actionedAt?.newValue
      ) {
        const actionedTime = new Date(
          trade.changes.actionedAt.newValue,
        ).getTime();
        const isRecent = Date.now() - actionedTime < 5000;

        if (isRecent) {
          handleTradeNotification({
            newStatus: trade.currentStatus as ExecutionStrategyStatus,
            tradeId: trade.id,
            role: trade.role,
            owner: trade.owner,
            latestActivity: "Re-Quoted",
            showConsoleLog: true,
          });
        }
      }

      // Currently this is getting used for when a block is flagged for / or removed as ReCoupon mid way through the trade.
      const unwind = trade.unwind;
      // Section for trades that have had their ReCoupon status changed through the trade life cycle and NO LONGER contain a recoupon block.
      if (!unwind) {
        handleRedrawingRowsWhenNoStatusChanges({
          trade,
          gridRef,
          expandedRowsContext,
        });
      }

      // Orders OP: Handle fills arriving while at Working status (No status change). handleOPOrderFillsArriving used above for when status DOES change.
      if (
        trade.role === "OP" &&
        trade.tradeType === TradeType.Order &&
        trade.currentStatus === ExecutionStrategyStatus.Working
      ) {
        handleOPOrderFillsArriving(trade, gridRef);
      }

      // Orders RP: Redraw detail grid when backend assigns new fillIds (no status change)
      // Only fires when fillIds change (backend update), not on frontend edits (no flicker)
      if (
        trade.role === "RP" &&
        trade.tradeType === TradeType.Order &&
        trade.currentStatus === ExecutionStrategyStatus.Working
      ) {
        handleRPOrderFillsUpdated(trade, gridRef);
      }
    }
    // END -  Can be moeved to a helper?? // Currently this is getting used for when a block is flagged for / or removed as ReCoupon mid way through the trade.

    // ONLY manange the API for Col visiblity if there is a status change.
    if (trade.changes.status != undefined) {
      handleShowOrHideCols(trade, gridRef);

      // const newStatus = trade.changes.status.newValue;
      // const isMovingToAwardedOrExecuted =
        // newStatus === ExecutionStrategyStatus.Awarded ||
      //   statusAtExecutedRange.includes(newStatus);
      // if (isMovingToAwardedOrExecuted) {
        
        if (trade.role === "RP") {
          syncERDetailRowData(esid, gridRef);
          refreshCellInLegLevel(esid, gridRef, ["quotedSize"], true);
          refreshColInERLevel(esid, gridRef, ["quotedSizeOutright", "quotedSize"], true);

        }
      // }
    }

    if (trade.changes.quotedSize) {
      const field = "quotedSize";
      flashCellInLegLevel(esid, gridRef, field);
    } else if (trade.changes.executionRequestCounterParties) {
      // redrawRowsViaESID(trade.id, gridRef);
      const field = "dealtStrikeOutright";
      flashCellsInERLevel(esid, gridRef, field);
    }

    // LQC-275: re-evaluate the OP % Quoted columns when leg data changes.
    // findChangedDataForMainActionSummary uses reference equality on nested
    // objects, so a leg's quotedSize change surfaces here as an
    // `executionRequestCounterParties` change rather than a top-level `quotedSize`.
    if (
      trade.role === "OP" &&
      (trade.changes.executionRequestCounterParties || trade.changes.quotedSize)
    ) {
      const liveTrade = tradeDataStore
        .getState()
        .tradeData.find((t) => t.esid === esid);
      if (shouldShowPercentQuoted(trade.tradeType, liveTrade)) {
        showOnLeg(esid, gridRef, ["percentQuoted"]);
        if (trade.strategyType == StrategyType.Outright) {
          showOnER(esid, gridRef, ["percentQuotedOutright"]);
        }
      } else {
        hideOnLeg(esid, gridRef, ["percentQuoted"]);
        if (trade.strategyType == StrategyType.Outright) {
          hideOnER(esid, gridRef, ["percentQuotedOutright"]);
        }
      }
    }

    // LQC-922: re-evaluate the OP Iceberg ER column when CP data changes.
    // releaseSize lives on the CP, so changes arrive via
    // `executionRequestCounterParties`.
    if (trade.role === "OP" && trade.changes.executionRequestCounterParties) {
      const liveTrade = tradeDataStore
        .getState()
        .tradeData.find((t) => t.esid === esid);
      const isOutrightRFQ =
        liveTrade?.type === TradeType.RFQ &&
        liveTrade?.strategyType === StrategyType.Outright;
      const icebergCol = isOutrightRFQ
        ? "percentIcebergOutright"
        : "percentIceberg";
      if (tradeHasIceberg(liveTrade)) {
        showOnER(esid, gridRef, [icebergCol]);
      } else {
        hideOnER(esid, gridRef, [icebergCol]);
      }
    }

    // LQC-1632: re-sync the RP cover/coverSpread/competition ("Comp") columns on
    // every non-status redraw so an ADVISE COMP edit shows/hides live. Not gated on
    // the per-field diff — a cleared value may not surface there. The function is
    // RP-gated and reads live values. Status changes go via handleShowOrHideCols.
    if (trade.changes.status == undefined) {
      handleCoverColumnVisibility(trade, gridRef);
    }
  }
}
